"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { ArrowRight, ThumbsDown, ThumbsUp } from "@/components/icons";
import { ANALYTICS_EVENTS } from "@shared/analytics/events";
import {
  FAQ_QUESTION_MAX_LENGTH,
  FAQ_QUESTION_MIN_LENGTH,
} from "@shared/faq/ask-protocol";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { track } from "@/lib/analytics/tracker";
import { openPublicChat } from "@/lib/chat/open-public-chat";
import { cn } from "@/lib/utils";
import {
  FAQ_ROW_STYLES,
  FAQ_SUMMARY_BASE,
  type FaqRowVariant,
} from "./faq-row-styles";
import { useFaqAsk, type AskedQuestion } from "./use-faq-ask";

const STATE_MESSAGES: Partial<Record<AskedQuestion["state"], string>> = {
  failed: "That did not go through. Try again, or ask Repackify support.",
  rate_limited:
    "You have asked a lot of questions in the last hour. Try again later, or ask Repackify support now.",
  busy: "The FAQ assistant is busy right now. Try again in a few minutes, or ask Repackify support.",
  blocked:
    "We could not process that question. Remove any personal details and rephrase it.",
  invalid_request: "We could not process that question. Try rephrasing it.",
};

function AnswerRating({ entry }: { entry: AskedQuestion }) {
  const [rating, setRating] = useState<"helpful" | "unhelpful" | null>(null);
  if (!entry.questionId) return null;
  const questionId = entry.questionId;

  const rate = (value: "helpful" | "unhelpful") => {
    setRating(value);
    track(ANALYTICS_EVENTS.FAQ_ANSWER_RATED, {
      question_id: questionId,
      rating: value,
    });
  };

  return (
    <div className="flex items-center gap-1">
      <IconButton
        variant="quiet"
        size="compact"
        aria-label="This answer helped"
        aria-pressed={rating === "helpful"}
        onClick={() => rate("helpful")}
      >
        <ThumbsUp aria-hidden />
      </IconButton>
      <IconButton
        variant="quiet"
        size="compact"
        aria-label="This answer did not help"
        aria-pressed={rating === "unhelpful"}
        onClick={() => rate("unhelpful")}
      >
        <ThumbsDown aria-hidden />
      </IconButton>
    </div>
  );
}

function SourceLinks({ sources }: { sources: AskedQuestion["sources"] }) {
  if (sources.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-3">
      <span>Read more:</span>
      {sources.map((source) => (
        <Link
          key={source.path}
          href={source.path}
          className="text-link hover:text-link-hover hover:underline"
        >
          {source.title}
        </Link>
      ))}
    </div>
  );
}

function AskedRow({
  entry,
  variant,
}: {
  entry: AskedQuestion;
  variant: FaqRowVariant;
}) {
  const styles = FAQ_ROW_STYLES[variant];
  const message = STATE_MESSAGES[entry.state];
  const isSettled = entry.state !== "streaming";
  const canHandOff =
    isSettled && entry.state !== "blocked" && entry.state !== "invalid_request";

  return (
    <details open className="group">
      <summary className={cn(FAQ_SUMMARY_BASE, styles.summary)}>
        <span>{entry.question}</span>
        {isSettled ? null : <Spinner variant="sm" aria-label="Answering" />}
      </summary>
      <div className={styles.body}>
        <p>{message ?? entry.answer}</p>
        <SourceLinks sources={entry.sources} />
        {isSettled ? (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            {canHandOff ? (
              <Button
                type="button"
                variant="link"
                size="bare"
                onClick={() => openPublicChat(entry.question)}
              >
                Ask Repackify support
              </Button>
            ) : (
              <span />
            )}
            <AnswerRating entry={entry} />
          </div>
        ) : null}
      </div>
    </details>
  );
}

function AskForm({
  variant,
  draft,
  canAsk,
  onDraftChange,
  onSubmit,
}: {
  variant: FaqRowVariant;
  draft: string;
  canAsk: boolean;
  onDraftChange: (draft: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      className={cn(
        "flex items-center gap-3",
        variant === "plain" ? "py-2" : "px-4 py-2",
      )}
    >
      <Input
        aria-label="Ask a question"
        placeholder="Ask anything else"
        value={draft}
        maxLength={FAQ_QUESTION_MAX_LENGTH}
        enterKeyHint="send"
        autoComplete="off"
        onChange={(event) => onDraftChange(event.target.value)}
      />
      <IconButton
        type="submit"
        variant="primary"
        size="default"
        aria-label="Ask"
        disabled={!canAsk}
      >
        <ArrowRight aria-hidden />
      </IconButton>
    </form>
  );
}

function announcementFor(latest: AskedQuestion | undefined): string {
  if (!latest) return "";
  if (latest.state === "streaming") return "Answering your question.";
  return STATE_MESSAGES[latest.state] ?? latest.answer;
}

export function FaqAskRow({ variant }: { variant: FaqRowVariant }) {
  const { asked, ask, isStreaming } = useFaqAsk();
  const [draft, setDraft] = useState("");
  const question = draft.trim();
  const canAsk = !isStreaming && question.length >= FAQ_QUESTION_MIN_LENGTH;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canAsk) return;
    setDraft("");
    void ask(question);
  };


  return (
    <>
      {asked.map((entry) => (
        <AskedRow key={entry.id} entry={entry} variant={variant} />
      ))}
      <p role="status" className="sr-only">
        {announcementFor(asked.at(-1))}
      </p>
      <AskForm
        variant={variant}
        draft={draft}
        canAsk={canAsk}
        onDraftChange={setDraft}
        onSubmit={submit}
      />
    </>
  );
}
